import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Leaf, ArrowLeft, HelpCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { submitLoginHelp } from '@/utils/api';

const LoginHelpPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await submitLoginHelp(email, description || null);
      setSubmitted(true);
      toast.success('Help request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Leaf className="w-10 h-10 text-primary" />
            <span className="text-3xl font-black tracking-tight text-primary">insul</span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-card rounded-3xl shadow-2xl p-8 border border-border/50">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6 -ml-2"
            onClick={() => navigate('/login')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Request Submitted</h2>
                <p className="text-muted-foreground">
                  We've received your help request for <strong>{email}</strong>
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
                <p className="text-sm text-blue-800 font-medium mb-2">What happens next?</p>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>An administrator will review your request</li>
                  <li>If your account is locked, it will be unlocked</li>
                  <li>You may receive a password reset link</li>
                </ul>
              </div>
              <div className="space-y-3 pt-4">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full rounded-full bg-gradient-to-r from-primary to-[#8FD14F]"
                >
                  Return to Login
                </Button>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  Try Password Reset
                </Button>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <HelpCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-2xl font-bold mb-2">Can't Log In?</h1>
                <p className="text-muted-foreground">
                  Let us know and we'll help you get back into your account
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Your Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-xl"
                    data-testid="help-email-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    What's happening? (Optional)
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="e.g., I forgot my password, my account seems locked, I'm getting an error message..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="rounded-xl min-h-[100px]"
                    data-testid="help-description-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full py-6 bg-gradient-to-r from-primary to-[#8FD14F]"
                  data-testid="submit-help-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Help Request'
                  )}
                </Button>
              </form>

              {/* Additional Options */}
              <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
                <p className="text-sm text-center text-muted-foreground mb-4">
                  You can also try:
                </p>
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => navigate('/forgot-password')}
                >
                  Reset My Password
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Remember your password?{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Log in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default LoginHelpPage;
